import { CallHistory } from '@/components/dashboard/call-history/call-history';
import { useSidePanelDispatch } from '@/components/sidepanel/side-panel-provider';
import { PageContent, PageHeader, PageLayout } from '@/components/ui/design-system';
import { useCanCall } from '@/hooks/calls/use-can-call';
import { useTranslation } from '@/hooks/use-translation';
import { useAppCallActions } from '@/providers/app-call-actions/use-app-call-actions';
import { useCallCapabilities } from '@/providers/call-capabilities/use-call-capabilities';
import { useLines } from '@/providers/lines/use-lines';
import type { PhoneNumberData } from '@/types/calls-types';
import { SummaryDetailView } from '@/views/summaries/summary-detail-view';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

export const Route = createFileRoute('/_app/calls')({
  component: RouteComponent,
});

function RouteComponent() {
  const { currentLine } = useLines();
  const { call } = useAppCallActions();
  const { t } = useTranslation('desktop_ui');

  const { canCall } = useCanCall();
  const { addFailedToCall } = useCallCapabilities();
  const setSidePanelState = useSidePanelDispatch();
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const onStartCall = async (phoneNumber: PhoneNumberData) => {
    if (canCall.ok) {
      await call(currentLine, phoneNumber);
    } else {
      addFailedToCall(canCall.reason);
    }
  };

  const handleOpenSheet = (contactId?: string) => {
    setSidePanelState({
      open: true,
      context: {
        contactId,
      },
    });
  };

  const handleCloseSummary = useCallback(() => {
    setSelectedCallId(null);
  }, []);

  useEffect(() => {
    setSidePanelState({
      open: false,
      context: {
        contactId: null,
      },
    });
  }, []);

  return (
    <div className="relative h-full w-full">
      {selectedCallId && (
        <div className="absolute inset-0 z-dropdown">
          <SummaryDetailView
            callId={selectedCallId}
            onBack={handleCloseSummary}
            source="recents"
            parentLabel={t('components.sidebar.menu.calls')}
          />
        </div>
      )}
      <PageLayout>
        <PageHeader title={t('components.sidebar.menu.calls')} />
        <PageContent>
          <div className="flex flex-col flex-1 h-full">
            <CallHistory
              currentLine={currentLine}
              makeOutboundCall={onStartCall}
              onOpenContact={handleOpenSheet}
              onOpenSummary={setSelectedCallId}
            />
          </div>
        </PageContent>
      </PageLayout>
    </div>
  );
}
